import React from "react";
import { useSelector } from "react-redux";
import { IContextualMenuItem } from "@fluentui/react";
import { DocubirdCache } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { CacheHelper } from "../../../utilities/CacheHelper";
import { automationTargetsOf, getAutomationChoices } from "../../../src/taskpane/services/HooksService";
import { containerTargetFromDirectory } from "../../../src/taskpane/services/automationLocation";
import { IFileApproval } from "../../../src/taskpane/components/_v2/components/Panels/AutomationPanel/AutomationPanel";
import { menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { FlowDot20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/FlowDot20Regular";

// Automation on the open location (nothing selected): the open document set or folder, or the library.
// Unlike Open in SharePoint it is not hidden for Docubird-managed libraries.
export function useMenuItem_AutomationLocation(
    isEnabled: boolean,
    isInOneDrive: boolean,
    waitFor: boolean,
    openAutomationAtLocation: (container: IFileApproval | null) => void
) {
    const languageStrings = React.useContext(LanguageContext);
    const tenantEnableApprovalHooks = useSelector((state: RootState) => state.user.tenantEnableApprovalHooks);
    const currentDirectory = useSelector((state: RootState) => (
        state.workingDirectories.workingDirectories.length > 0
            ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1]
            : null
    ));
    // Tess entries carry a fixed folder content type, not the dossier's own: no location start there,
    // as in the add-in's folder panel.
    const isUnderTess = useSelector((state: RootState) =>
        state.workingDirectories.workingDirectories.some(directory => directory?.tessAccountId || directory?.tessProjectId));
    // Flips when the hook list in CacheHelper is refreshed: with nothing selected, nothing else re-reads it.
    const cacheUpdated = useSelector((state: RootState) => state.cacheUpdate?.toggle);

    const menuItem_AutomationLocation = React.useMemo(() => {
        if (!isEnabled || isInOneDrive || isUnderTess || !tenantEnableApprovalHooks || !currentDirectory?.siteDisplayName || (!currentDirectory?.DocumentLibrary && !currentDirectory?.sharepointList)) {
            return null;
        }

        const container = containerTargetFromDirectory(currentDirectory);
        const allHooks = CacheHelper.get(DocubirdCache.hooks);
        const choices = getAutomationChoices(allHooks, currentDirectory, container ? automationTargetsOf([container]) : [], currentDirectory);
        if (choices.length === 0) {
            return null;
        }

        return {
            key: "automationLocation",
            disabled: waitFor === true,
            text: languageStrings.Automation,
            iconProps: {},
            onRenderIcon: () => <FlowDot20Regular className={menuItemIconStyles} />,
            onClick: () => openAutomationAtLocation(container)
        } as IContextualMenuItem;
    }, [
        isEnabled,
        isInOneDrive,
        isUnderTess,
        waitFor,
        currentDirectory,
        tenantEnableApprovalHooks,
        cacheUpdated,
        languageStrings,
        openAutomationAtLocation
    ]);

    return {
        menuItem_AutomationLocation
    };
}
